import React from 'react';
interface SkeletonProps {
  /** Width class (Tailwind) — default: 'w-full' */
  className?: string;
  /** Variant: 'text' (single line), 'circle', 'rect' (card-sized block) */
  variant?: 'text' | 'circle' | 'rect';
  /** Number of text lines to render (only for variant='text') */
  lines?: number;
}
/**
 * Skeleton loading placeholder — shows animated pulse blocks.
 * Use inside portal pages while data is loading.
 */
export function Skeleton({
  className = '',
  variant = 'text',
  lines = 1
}: SkeletonProps) {
  if (variant === 'circle') {
    return <div className={`rounded-full bg-slate-200 animate-pulse ${className}`} aria-hidden="true" data-id="element-2528" data-mp="1d83h8ioub4ek" />;
  }
  if (variant === 'rect') {
    return <div className={`rounded-2xl bg-slate-200 animate-pulse ${className}`} aria-hidden="true" data-id="element-2529" data-mp="1m6yntvvsqgie" />;
  }
  // Text variant — render multiple lines
  return <div className={`space-y-2.5 ${className}`} aria-hidden="true" data-id="element-2530" data-mp="3nyv7xtw14yos">
      {Array.from({
      length: lines
    }).map((_, i) => <div key={i} className={`h-4 bg-slate-200 rounded-md animate-pulse ${i === lines - 1 && lines > 1 ? 'w-3/4' : 'w-full'}`} data-id="element-2531" data-mp="1t5s1w8hxc34o" data-mpi={i} />)}
    </div>;
}
/**
 * Skeleton card — mimics a StatCard or summary card while loading.
 */
export function SkeletonCard({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`bg-white border border-slate-200 rounded-2xl p-6 shadow-sm ${className}`} aria-hidden="true" data-id="element-2532" data-mp="2bz5ljhg6sn4z">
      <div className="flex items-center gap-4" data-id="element-2533" data-mp="2gvw54er7a0v5">
        <Skeleton variant="circle" className="w-12 h-12" data-id="element-2534" data-mp="2lwtlqmwkamuj" />
        <div className="flex-1 space-y-2" data-id="element-2535" data-mp="2lxbqy917cbel">
          <Skeleton className="w-24" data-id="element-2536" data-mp="2qw6h5w1i128q" />
          <Skeleton className="w-16 h-6" data-id="element-2537" data-mp="2qv4supvh2j92" />
        </div>
      </div>
    </div>;
}
/**
 * Skeleton page — full page loading state for portal pages.
 * Shows 3 summary cards + a content block.
 */
export function SkeletonPage() {
  return <div className="max-w-7xl mx-auto p-6 space-y-8" aria-busy="true" aria-label="Laden..." data-id="element-2538" data-mp="ogxvnn8dkcwe">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2539" data-mp="jiwa8vnh81hr">
        <SkeletonCard data-id="element-2540" data-mp="yeu5mbpkt1sx" />
        <SkeletonCard data-id="element-2541" data-mp="yfdkfjjxja2q" />
        <SkeletonCard data-id="element-2542" data-mp="ygorbs8o8xy2" />
      </div>

      {/* Content block */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4" data-id="element-2543" data-mp="tfhfni3erem4">
        <Skeleton className="w-48 h-5" data-id="element-2544" data-mp="17fa4f7vdjkrg" />
        <div className="space-y-3" data-id="element-2545" data-mp="17e7h19x9g0ga">
          {Array.from({
          length: 5
        }).map((_, i) => <div key={i} className="flex items-center gap-4" data-id="element-2546" data-mp="12emgww6ctm1z" data-mpi={i}>
              <Skeleton variant="circle" className="w-10 h-10" data-id="element-2547" data-mp="1h4jyuudoxfiq" />
              <div className="flex-1" data-id="element-2548" data-mp="1c6s6jz94vre0">
                <Skeleton className="w-32" data-id="element-2549" data-mp="1c7tjk99pmnyd" />
              </div>
              <Skeleton variant="rect" className="w-24 h-3" data-id="element-2550" data-mp="1q607awky37kb" />
              <Skeleton className="w-10 h-5" data-id="element-2551" data-mp="1q470nhac0ncb" />
            </div>)}
        </div>
      </div>
    </div>;
}